import { API_URL, COUNTERS_LAMBDA_URL, STN_LAMBDA_URL } from '@/constants/config';
import { getAuthHeader } from '~/modules/common/utils/cookieUtils';
import { encodeQuery } from '~/modules/common/utils/routingUtils';

const base = API_URL + '/v3.5';
const stnLambdaUrl = STN_LAMBDA_URL;

/**
 * Article Service
 *
 * @param {object} http axios instance from nuxt context
 * @param {object} cookies cookies from nuxt context
 */
export const articleService = (http, cookies) => ({
  /**
   * Get Articles List
   *
   * @param {object} param object
   * @param {number} param.type article types
   * @param {number} [param.page] page optional
   * @param {number} [param.perPage] page optional
   */
  async getArticles({ type, page = 1, perPage = 50 }) {
    const queryParams = encodeQuery({ type, page, perPage });
    const { data } = await http.$get(`${base}/articles/${queryParams}`);
    return { data };
  },

  /**
   * Get article by id
   * @param {string} id article id
   */
  async getArticle(id) {
    const { data } = await http.$get(`${base}/articles/${id}`);
    return { data };
  },

  /**
   * Get astorology article
   * @param {string} horoscope horoscope
   * @param {string} date date
   * @param {string} frequency frequency
   * @param {string} type type
   */
  async getAstrologyArticle({ horoscope, date, frequency, type }) {
    const { data } = await http.$get(
      `${base}/articles/periodic-contents/detail?periodicContentKey=astrology:horoscope:${horoscope}:${type}:${frequency}:${date}`
    );
    return { data };
  },

  /**
   * Get tarot article
   */
  async getTarotArticle({ card, route }) {
    const routeWithoutCard = route.replace(`/${card}`, '');
    const tarotParams = `periodicContentRoute=${routeWithoutCard}`;
    const cardParams = card ? `&periodicContentEntryRoute=${route}` : '';

    const { data } = await http.$get(`${base}/articles/periodic-contents/detail?${tarotParams}${cardParams}`);
    return { data };
  },

  /**
   * Get Recommended Article by legacy id for video player
   * @param {number} id article legacy id
   */
  async getRecommendedArticle(id) {
    const { data } = await http.$get(`${base}/browse/recommended/videos/${id}`);
    return data;
  },

  /**
   * Get Trends Article
   *
   * @param {object} param object
   * @param {number} [param.page] page page optional
   */
  async getTrends({ page = 1, description, reactions, comments, favoriteCheck }) {
    const queryParams = encodeQuery({ page, description, reactions, comments, author: true, categories: true, favoriteCheck });
    const { data, pagination } = await http.$get(`${base}/browse/trends${queryParams}`, { headers: getAuthHeader(cookies) });
    return { data, pagination };
  },

  /**
   * Get All Articles
   *
   * @param {object} param object
   * @param {number} [param.page] page page optional
   */
  async getAllArticles({ page = 1, comments, favoriteCheck, reactions }) {
    const queryParams = encodeQuery({
      page,
      useHasNextPage: true,
      author: true,
      categories: true,
      breadcrumb: true,
      comments,
      favoriteCheck,
      reactions,
    });
    const { data, pagination } = await http.$get(`${base}/articles${queryParams}`, { headers: getAuthHeader(cookies) });
    return { data, pagination };
  },

  /**
   * Get STN articles by article id
   * @param {object} param object
   * @param {string} param.id article id
   * @param {string} [param.categoryId] article category id
   * @param {string} [param.tagId] article tagId id
   * @param {string} [param.deviceType] deviceType
   */
  async getSTNArticles({ id, categoryId, tagId, deviceType } = {}) {
    const queryParams = encodeQuery({ categoryId, deviceType, tagId });
    const data = await http.$get(`${stnLambdaUrl}/${id}${queryParams}`);
    return { data: data.result };
  },

  /**
   * Get article favorite status
   * @param {string} articleId
   * @returns
   */
  async getFavoriteStatus(articleId) {
    const { data } = await http.$get(`${base}/favorites/check/${articleId}`, { headers: getAuthHeader(cookies) });
    return { data };
  },

  /**
   * Favorite article
   * @param {string} articleId article id
   */
  async favoriteArticle(articleId) {
    const { data } = await http.$post(`${base}/favorites/article/${articleId}`, {}, { headers: getAuthHeader(cookies) });
    return { data };
  },

  /**
   * Unfavorite article
   * @param {string} articleId article id
   */
  async unfavoriteArticle(articleId) {
    const { data } = await http.$delete(`${base}/favorites/article/${articleId}`, { headers: getAuthHeader(cookies) });
    return { data };
  },

  /**
   * Get Comments
   * @param {object} param object
   * @param {number} param.id article id
   * @param {string} param.sort sort - recent, popular
   * @param {number} [param.page] page optional
   * @param {number} [param.limit] limit optional
   */
  async getComments({ id, sort = 'recent', page, limit } = {}) {
    const queryParams = encodeQuery({ articleId: id, page, limit, sort, useHasNextPage: true, checkBlockStatus: true });
    const { data, pagination } = await http.$get(`${base}/comments${queryParams}`, { headers: getAuthHeader(cookies) });
    return { data, pagination };
  },

  /**
   * Get Replies
   * @param {object} param object
   * @param {number} param.id article id
   * @param {number} [param.page] page optional
   * @param {number} [param.limit] limit optional
   */
  async getReplies({ id, page, limit = 10 } = {}) {
    const queryParams = encodeQuery({ page, limit, useHasNextPage: true, checkBlockStatus: true });
    const { data, pagination } = await http.$get(`${base}/comments/${id}/replies${queryParams}`, { headers: getAuthHeader(cookies) });
    return { data, pagination };
  },

  /**
   * Send Comment
   *
   * @param {object} params object
   * @param {string} params.id article id
   * @param {string} params.text comment
   *
   */
  async sendComment({ id, text } = {}) {
    const body = {
      comment: text,
      articleId: id,
    };

    const { data } = await http.$post(`${base}/comments`, body, {
      headers: getAuthHeader(cookies),
    });

    return { data };
  },

  /**
   * Send Reply
   *
   * @param {object} params object
   * @param {string} params.text comment
   * @param {string} params.parentId
   * @param {string} params.replyToId
   *
   */
  async sendReply({ text, parentId, replyToId } = {}) {
    const body = {
      comment: text,
      replyTo: replyToId,
    };

    const { data } = await http.$post(`${base}/comments/${parentId}/replies`, body, {
      headers: getAuthHeader(cookies),
    });

    return { data };
  },

  /**
   * Like comment
   *
   * @param {string} id comment id
   */
  async likeComment(id) {
    const { data } = await http.$post(`${base}/comments/${id}/like`, {}, { headers: getAuthHeader(cookies) });
    return { data };
  },

  /**
   * Unlike comment
   *
   * @param {string} id comment id
   */
  async unLikeComment(id) {
    const { data } = await http.$delete(`${base}/comments/${id}/like`, { headers: getAuthHeader(cookies) });
    return { data };
  },

  /**
   * Report comment
   *
   * @param {string} id comment id
   */
  async reportComment(id) {
    const { data } = await http.$post(`${base}/comments/${id}/report`, {}, { headers: getAuthHeader(cookies) });
    return { data };
  },

  /**
   * Delete comment
   *
   * @param {string} id comment id
   */
  async deleteComment(id) {
    const { data } = await http.$delete(`${base}/comments/${id}`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Add Reaction
   *
   * @param {string} articleId article id
   * @param {string} reactionId reaction id
   */
  async addReaction(articleId, reactionId) {
    const { data } = await http.get(`${COUNTERS_LAMBDA_URL}/hit?key=article:${articleId}:reaction:${reactionId}`, {
      withCredentials: true,
      credentials: 'include',
    });
    return { data };
  },

  /**
   * Remove Reaction
   *
   * @param {string} articleId article id
   * @param {string} reactionId reaction id
   */
  async removeReaction(articleId, reactionId) {
    const { data } = await http.get(`${COUNTERS_LAMBDA_URL}/pop?key=article:${articleId}:reaction:${reactionId}`, {
      withCredentials: true,
      credentials: 'include',
    });
    return { data };
  },

  /**
   * Get article's sidebar recommendations
   * @param {string} id - article id
   * @param {string} tagId - tag id for sponsored articles
   */
  async getArticleSidebarRecommendations(id, { type, sponsoredId, limit = 25 }) {
    let queryParams;
    if (type === 'tag') {
      queryParams = encodeQuery({ tagId: sponsoredId, duration: 'monthly', author: true, categories: true, breadcrumb: true, limit });
    } else if (type === 'category') {
      queryParams = encodeQuery({ categoryId: sponsoredId, duration: 'monthly', author: true, categories: true, breadcrumb: true, limit });
    } else {
      queryParams = encodeQuery({ author: true, categories: true, breadcrumb: true, limit });
    }
    const { data } = await http.$get(`${base}/browse/recommendations/${id}${queryParams}`);
    return { data };
  },

  /**
   * Get Mega Footer Data
   */
  async getFooterData() {
    const { data } = await http.$get(`${base}/browse/footer`);
    return { data };
  },

  /**
   * GetBreaking News in Header
   */
  async getBreakingNews() {
    const { data, extraData } = await http.$get(`${base}/browse/breaking-news`);
    return { data, extraData };
  },

  /**
   * Get reel by id
   * @param {string} id reel id
   */
  async getReel(id) {
    const { data } = await http.$get(`${base}/reels/${id}?reactions=true`);
    return { data };
  },

  /**
   * Get segments by name
   * @param {string} str segment name
   */

  async getSegmentsAudienceID(str) {
    let data;
    if (Array.isArray(str)) {
      data = Promise.all(
        str.map(async (segment) => {
          const arr = segment.split('|');
          const { data } = await http.$get(`${base}/dmp/segments?name=${arr[arr.length - 1]}`);
          return data.map((segment) => segment.audience).toString();
        })
      );
    }
    return { data: await data };
  },
});
